<template>
    <nav class="navbar bg-white border-bottom sticky-top">
        <div class="container-fluid">
            <router-link class="navbar-brand" to="/">
                <img :src="require('@/assets/logo.png')" alt="logo" style="height: 40px;">
            </router-link>
            <div class="d-flex align-items-center">
                <a v-if="showWorkTimeClock" href="#" class="me-3" role="button"
                    :aria-label="$t('workTime.pickTitle')" @click.prevent="openWorkTimePicker">
                    <i v-if="isNonDefaultPick" class="fa-solid fa-clock fa-lg"></i>
                    <i v-else class="fa-regular fa-clock fa-lg text-secondary"></i>
                </a>
                <router-link class="me-2" to="/notifications" @click="closeNavbar">
                    <i v-if="unreadNotificationsCount > 0" class="fa-solid fa-bell fa-lg text-danger"></i>
                    <i v-else class="fa-regular fa-bell fa-lg"></i>
                </router-link>
                <button class="navbar-toggler border-0" type="button" @click="toggleNavbar">
                    <i class="fa-solid fa-bars fa-lg"></i>
                </button>
            </div>
        </div>
    </nav>
    <div class="offcanvas offcanvas-start bg-light" :class="{ 'show': navbarOpen }" id="navbarNav">
        <div class="offcanvas-header">
            <h5 class="offcanvas-title"></h5>
            <button type="button" class="btn-close" @click="closeNavbar" aria-label="Close"></button>
        </div>
        <div class="offcanvas-body">
            <ul class="navbar-nav">
                <li class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/" @click="closeNavbar"><i
                            class="fa-solid fa-house fa-fw me-2"></i>{{ $t('nav.dashboard') }}</router-link>
                </li>
                <li v-if="can('Priv_AppCalendar')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/calendar" @click="closeNavbar"><i
                            class="fa-regular fa-calendar-days fa-fw me-2"></i>{{ $t('nav.calendar') }}</router-link>
                </li>
                <li v-if="can('Priv_AppMaps')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/maps" @click="closeNavbar"><i
                            class="fa-solid fa-map-location-dot fa-fw me-2"></i>{{ $t('nav.maps') }}</router-link>
                </li>
                <li v-if="can('Priv_AppRealestates')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/realestates" @click="closeNavbar"><i
                            class="fa-regular fa-building fa-fw me-2"></i>{{ $t('nav.buildings') }}</router-link>
                </li>
                <li v-if="can('Priv_AppProjects')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/projects" @click="closeNavbar"><i
                            class="fa-solid fa-file-invoice fa-fw me-2"></i>{{ $t('nav.projects') }}</router-link>
                </li>
                <li v-if="can('Priv_AppObjects')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/objects" @click="closeNavbar"><i
                            class="fa-solid fa-walkie-talkie fa-fw me-2"></i>{{ $t('nav.devices') }}</router-link>
                </li>
                <li v-if="can('Priv_AppWarehouse')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/stock-items" @click="closeNavbar"><i
                            class="fa-solid fa-warehouse fa-fw me-2"></i>{{ $t('nav.warehouse') }}</router-link>
                </li>
                <li class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/tasks-finished" @click="closeNavbar"><i
                            class="fa-solid fa-list-check fa-fw me-2"></i>{{ $t('nav.finishedTasks') }}</router-link>
                </li>
                <li v-if="can('Priv_AppWorkload')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/workload" @click="closeNavbar"><i
                            class="fa-regular fa-clock fa-fw me-2"></i>{{ $t('nav.workload') }}</router-link>
                </li>
                <li v-if="can('Priv_AppObjects')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/scan" @click="closeNavbar"><i
                            class="fa-solid fa-qrcode fa-fw me-2"></i>QR</router-link>
                </li>
                <li v-if="can('Priv_AppDocumentation')" class="nav-item border-bottom py-3">
                    <a target="_blank" href="https://ussee.sharepoint.com/sites/Manuals/Shared Documents/Forms/AllItems.aspx" class="nav-link" @click="closeNavbar"><i
                            class="fa-solid fa-book fa-fw me-2"></i>{{ $t('nav.documentation') }}</a>
                </li>
                <li v-if="can('Priv_AppProfile')" class="nav-item border-bottom py-3">
                    <router-link class="nav-link" to="/profile" @click="closeNavbar"><i
                            class="fa-regular fa-user fa-fw me-2"></i>{{ $t('nav.profile') }}</router-link>
                </li>
                <li class="nav-item border-bottom py-3">
                    <a href="#" class="nav-link" @click.prevent="logout"><i
                            class="fa-solid fa-arrow-right-from-bracket fa-fw me-2"></i>{{ $t('nav.logout') }}</a>
                </li>
            </ul>
        </div>
    </div>
    <!-- One picker serves both the voluntary clock click and the mandatory
         day-change question. An explicit click wins when both are true, so the
         user who opened it themselves keeps their way out. -->
    <div v-if="showWorkTimePicker || stalePickPending" class="item-modal bg-light">
        <div class="modal-content">
            <pick-work-time-modal :articles="workTimeArticles" :current-id="timeResourceId"
                :mandatory="stalePickPending && !showWorkTimePicker" :saving="savingWorkTime"
                @pick="onPickWorkTime" @close="showWorkTimePicker = false" />
        </div>
    </div>
</template>

<script>
import { msalInstance } from '@/composables/msalConfig';
import axios from '@/services/axios';
import { can as canPerm } from '@/composables/usePerms';
import { clearTemplates } from '@/composables/useTextTemplates';
import PickWorkTimeModal from '@/components/PickWorkTimeModal.vue';
import {
    articles as workTimeArticles,
    isNonDefaultPick,
    stalePickPending,
    timeResourceId,
    clearUserStatus,
    fetchUserStatus,
    loadArticles,
    setTimeResource,
} from '@/composables/useUserStatus';

export default {
    name: 'MainNavbar',
    components: {
        PickWorkTimeModal
    },
    setup() {
        return { workTimeArticles, isNonDefaultPick, stalePickPending, timeResourceId };
    },
    data() {
        return {
            navbarOpen: false,
            unreadNotificationsCount: 0,
            interval: null,
            lastFetchTime: null,
            fetchInterval: 60000,
            // Temporarily hidden: the day-change question still fires, but the
            // clock is kept out of the navbar until the feature is released.
            showWorkTimeClock: false,
            showWorkTimePicker: false,
            savingWorkTime: false,
        };
    },
    methods: {
        async openWorkTimePicker() {
            this.closeNavbar();
            await loadArticles(); // cached after the first open
            this.showWorkTimePicker = true;
        },
        async onPickWorkTime(id) {
            this.savingWorkTime = true;
            try {
                await setTimeResource(id);
                this.showWorkTimePicker = false;
            } catch (error) {
                console.error('Error setting work time article:', error);
                // Leave the modal open — especially the mandatory one, which
                // must not be dismissed by a failed write.
                this.$root.$refs.globalAlert?.showAlert(this.$t('workTime.updateFailed'), 'danger');
            } finally {
                this.savingWorkTime = false;
            }
        },
        can(perm) {
            return canPerm(perm);
        },
        async logout() {
            const loginMethod = localStorage.getItem('loginMethod');

            if (loginMethod === 'microsoft' && msalInstance) {
                try {
                    await msalInstance.logoutPopup();
                } catch (error) {
                    console.error("MSAL Logout error", error);
                }
            }

            await this.completeLogout();
        },
        toggleNavbar() {
            this.navbarOpen = !this.navbarOpen;
        },
        closeNavbar() {
            this.navbarOpen = false;
        },
        async completeLogout() {
            try {
                await axios.post('/logout');
            } catch (error) {
                console.error("API Logout error", error);
            }

            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('loginMethod');
            // In-memory only, but drop it now so the next user cannot see the
            // previous one's templates in the window before their own load lands.
            clearTemplates();
            clearUserStatus();

            this.$router.push('/login');
        },
        async fetchUnreadNotificationsCount() {
            if (!this.currentUser || !this.currentUser.id) {
                return;
            }
            try {
                const response = await axios.get(
                    `/notifications/count/?read=0&user_id=${this.currentUser.id}`
                );
                this.unreadNotificationsCount = response.data.data.count;
                this.lastFetchTime = Date.now(); // Update the last fetch time
            } catch (error) {
                console.error('Error fetching unread notifications count:', error);
            }
        },
        handleVisibilityChange() {
            if (document.visibilityState === 'visible') {
                const timeSinceLastFetch = Date.now() - (this.lastFetchTime || 0);

                if (timeSinceLastFetch >= this.fetchInterval) {
                    // If enough time has passed, fetch immediately
                    this.fetchUnreadNotificationsCount();
                }

                this.startNotificationPolling();
            } else {
                this.stopNotificationPolling();
            }
        },
        startNotificationPolling() {
            if (!this.interval) {
                // Set up the interval if it's not already running
                this.interval = setInterval(this.fetchUnreadNotificationsCount, this.fetchInterval);
            }
        },
        stopNotificationPolling() {
            if (this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        },
    },
    mounted() {
        this.lastFetchTime = Date.now(); // Initialize the last fetch time
        this.fetchUnreadNotificationsCount(); // Fetch immediately on mount
        this.startNotificationPolling(); // Start polling

        // The navbar is mounted for every non-login page, so this is the
        // app-load (and post-login) work-time check. HomeView's existing poll
        // keeps it fresh from there on — no extra polling here.
        loadArticles();
        fetchUserStatus();
    },
    computed: {
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        }
    },
    created() {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    },
    beforeUnmount() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.stopNotificationPolling();
    }
};
</script>


<style scoped>
/* Same full-screen overlay the task detail and workload modals use. Sits above
   the sticky navbar and the offcanvas, so the mandatory variant really blocks. */
.item-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 5px;
    z-index: 1031;
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    max-height: 100%;
    width: 100%;
    padding: 20px;
    overflow-y: auto;
}
</style>